import type { ActionModalType } from 'actions/openModalAction';
import { FullPolicyData, Policy, Quote } from 'interfaces/customerDataTypes';
import { AUTH_TYPE, AuthType } from 'interfaces/authTypes';

export interface UserInterfaceState {
  fetchError?: any;
  ajaxCallsInProgress: number;
  disableButtons: boolean;
}

export interface LoginState {
  loginValidationErrors?: { [key: string]: string };
  loginProcessing: boolean;
  authLinkFailed: boolean;
  authLinkSuccess: boolean;
  loginError?: string;
}

export interface DocumentsState {
  emailDocumentsProcessing: boolean;
  emailDocumentsError: boolean;
  emailDocumentsSuccess: boolean;
}

export interface PaymentState {
  isPaymentSetupLoading: boolean;
  isBraintreeLoading: boolean;
  paymentGuid: string;
  isPaymentSuccess: boolean;
  paymentErrorCount: number;
  paymentReference?: string;
  paymentStatus?: string;
  isPaymentSetupError: boolean;
  cardLastFour?: string;
  cardType?: string;
  cardImageUrl?: string;
  cardExpiry?: string;
  iframeHeight?: number;
}

export interface CustomerDataState {
  policyDetailsLoading: boolean;
  policyDetailsError?: string;
  isPolicyDetailsError: boolean;
  selectedPolicy?: FullPolicyData;
  toggleAutoRenewLoading: boolean;
  togglePrintDocumentsLoading: boolean;
  customerInsuranceSummaryLoading: boolean;
  policies?: Policy[];
  quotes?: Quote[];
  customerInsuranceSummaryError: boolean;
  toggleAutoRenewalError?: string;
  togglePrintDocumentsError?: string;
  noPolicyFoundError: boolean;
}

export interface HpAuthenticationState {
  name: string;
  email: string;
  isTokenError: boolean;
  isAuthenticated: boolean;
  isAgentAuthenticatedOrCustomer: boolean;
  authLinkError?: string;
  isAgentAdmin: boolean;
  isAgent: boolean;
  authType: AuthType;
}

export interface CustomerSearchState {
  searchProcessing: boolean;
  searchError: boolean;
  searchSuccess: boolean;
  searchResults?: any; // More specific type to be defined
  searchValidationErrors?: { [key: string]: string };
}

export interface ActionModalState {
  isOpen: boolean;
  modalType?: ActionModalType;
  alertMessage?: string;
}

export interface PolicyState {
  renewPolicyLoading: boolean;
  renewPolicyError: boolean;
  renewPolicySuccess: boolean;
  renewalPaymentReference?: string;
  actionModal: ActionModalState;
}

export interface State {
  userInterface: UserInterfaceState;
  login: LoginState;
  documents: DocumentsState;
  payments: PaymentState;
  customerData: CustomerDataState;
  authentication: HpAuthenticationState;
  customerSearch: CustomerSearchState;
  policy: PolicyState;
}

// Split into different functions.
export function getInitialState(): State {
  return {
    userInterface: {
      fetchError: null,
      ajaxCallsInProgress: 0,
      disableButtons: false
    },
    login: {
      loginProcessing: false,
      authLinkFailed: false,
      authLinkSuccess: false
    },
    documents: {
      emailDocumentsProcessing: false,
      emailDocumentsError: false,
      emailDocumentsSuccess: false
    },
    payments: {
      isPaymentSetupLoading: false,
      isBraintreeLoading: true,
      paymentGuid: '',
      isPaymentSuccess: false,
      paymentErrorCount: 0,
      isPaymentSetupError: false
    },
    customerData: {
      policyDetailsLoading: false,
      isPolicyDetailsError: false,
      toggleAutoRenewLoading: false,
      togglePrintDocumentsLoading: false,
      customerInsuranceSummaryLoading: false,
      customerInsuranceSummaryError: false,
      noPolicyFoundError: false
    },
    authentication: {
      name: '',
      email: '',
      isAuthenticated: false,
      isTokenError: false,
      isAgentAuthenticatedOrCustomer: false,
      isAgentAdmin: false,
      isAgent: false,
      authType: AUTH_TYPE.NONE
    },
    customerSearch: {
      searchProcessing: false,
      searchError: false,
      searchSuccess: false
    },
    policy: {
      renewPolicyLoading: false,
      renewPolicyError: false,
      renewPolicySuccess: false,
      actionModal: {
        isOpen: false
      }
    }
  };
}
